import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { getMyMemberProfile } from '../../services/members';
import { getCaseTypes, getCaseAmount } from '../../services/settings';
import { createMemberCase, uploadCaseDocuments } from '../../services/cases';
import Toast from '../../components/Common/Toast';
import { 
  ArrowLeft, Upload, X, FileText, Loader2, 
  Info, CheckCircle2, UserCircle 
} from 'lucide-react';

const CreateCase = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // UI & Loading States
  const [loading, setLoading] = useState(false);
  const [fetchingAmount, setFetchingAmount] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [toast, setToast] = useState(null);
  
  // Data States
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [caseTypes, setCaseTypes] = useState([]);
  const [files, setFiles] = useState([]);
  
  // Form State
  const [formData, setFormData] = useState({
    type: '',
    beneficiary_id: 'self',
    target_amount: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setInitialLoad(true);
      
      // 1. Fetch member profile (for beneficiaries)
      const profile = await getMyMemberProfile();
      console.log('Profile data:', profile);
      
      // Handle different property names (Beneficiaries vs beneficiaries)
      const rawBeneficiaries = profile?.Beneficiaries || profile?.beneficiaries || [];
      console.log('Beneficiaries:', rawBeneficiaries);
      
      const beneficiariesWithSelf = [
        { id: 'self', full_name: 'Myself (Primary Member)', relationship: 'Self' },
        ...rawBeneficiaries.map(b => ({
          id: b.id,
          full_name: b.full_name,
          relationship: b.relationship,
        }))
      ];
      setBeneficiaries(beneficiariesWithSelf);
      
      // 2. Fetch case types
      const response = await getCaseTypes();
      console.log('Case types API response:', response);
      
      // Handle different response formats
      let types = [];
      if (Array.isArray(response)) {
        types = response;
      } else if (response && typeof response === 'object' && response.data) {
        types = response.data;
      } else if (response && typeof response === 'object' && response.value) {
        try {
          const parsed = JSON.parse(response.value);
          types = Array.isArray(parsed) ? parsed : [];
        } catch (e) {
          console.error('Failed to parse case types JSON:', e);
          types = [];
        }
      } else {
        // Fallback default types
        types = ['welfare', 'education', 'medical', 'funeral'];
        console.warn('Using default case types as fallback');
      }
      
      // Ensure we have an array
      if (!Array.isArray(types) || types.length === 0) {
        types = ['welfare', 'education', 'medical', 'funeral'];
        console.warn('Fallback to default case types');
      }
      
      console.log('Setting case types:', types);
      setCaseTypes(types);
      
    } catch (err) {
      console.error('Initialization Error:', err);
      // Set default types on error
      setCaseTypes(['welfare', 'education', 'medical', 'funeral']);
      setToast({ type: 'error', message: 'Failed to load data. Using default types.' });
    } finally {
      setInitialLoad(false);
    }
  };

  const handleTypeChange = async (type) => {
    setFormData(prev => ({ ...prev, type, target_amount: '' }));
    if (!type) return;

    try {
      setFetchingAmount(true);
      const amount = await getCaseAmount(type);
      setFormData(prev => ({ ...prev, target_amount: amount }));
    } catch (err) {
      console.error('Failed to get amount:', err);
      setToast({ type: 'error', message: 'Could not fetch default amount for this category' });
    } finally {
      setFetchingAmount(false);
    }
  };

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...newFiles]);
  };

  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.type) return setToast({ type: 'error', message: 'Please select a case category' });
    
    setLoading(true);
    try {
      // 1. Create the Case record
      const caseRes = await createMemberCase({
        type: formData.type,
        beneficiary_id: formData.beneficiary_id === 'self' ? null : formData.beneficiary_id,
        target_amount: formData.target_amount,
      });

      // 2. Upload documents if attached
      if (files.length > 0) {
        await uploadCaseDocuments(caseRes.id, files);
      }

      setToast({ type: 'success', message: 'Application submitted successfully' });
      
      // Redirect after a brief delay
      setTimeout(() => navigate('/dashboard/member'), 2000);
    } catch (err) {
      setToast({ type: 'error', message: err.response?.data?.error || 'Submission failed' });
    } finally {
      setLoading(false);
    }
  };

  if (initialLoad) {
    return (
      <div className="h-screen w-full flex flex-col items-center justify-center gap-4">
        <Loader2 className="animate-spin text-indigo-600" size={40} />
        <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">Preparing Application...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 md:p-10 space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
      
      {/* Navigation Header */}
      <div className="flex items-center justify-between">
        <button 
          onClick={() => navigate('/dashboard/member')} 
          className="group flex items-center gap-2 text-slate-400 hover:text-indigo-600 transition-colors font-medium text-sm"
        >
          <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" /> 
          Back to Dashboard
        </button>
        <div className="flex items-center gap-2 px-3 py-1 bg-slate-100 rounded-full text-slate-500 text-[10px] font-black uppercase tracking-wider">
          <UserCircle size={12} />
          {user?.member_number || 'Member Portal'}
        </div>
      </div>

      <header className="space-y-2">
        <h1 className="text-4xl font-black text-slate-900 tracking-tight">Request Welfare Support</h1>
        <p className="text-slate-500 font-medium">Complete the form below to file a claim for yourself or a registered beneficiary.</p>
      </header>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-5 gap-8">
        
        {/* Left Section: Application Details (3/5 width) */}
        <div className="lg:col-span-3 space-y-6">
          <section className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-xl shadow-slate-200/50 space-y-6">
            <h3 className="text-sm font-black uppercase tracking-widest text-indigo-600 mb-4 flex items-center gap-2">
              <FileText size={16} /> Case Details
            </h3>

            <div className="space-y-5">
              {/* Case Type Dropdown */}
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 px-1">Welfare Category</label>
                <select
                  value={formData.type}
                  onChange={e => handleTypeChange(e.target.value)}
                  className="w-full px-5 py-4 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-indigo-500 font-bold text-slate-700 appearance-none"
                  required
                >
                  <option value="">Select category...</option>
                  {caseTypes.length > 0 ? (
                    caseTypes.map(type => (
                      <option key={type} value={type}>
                        {type.replace(/_/g, ' ').toUpperCase()}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>No categories available</option>
                  )}
                </select>
                <p className="text-[9px] text-slate-300 mt-1 px-1">
                  Loaded {caseTypes.length} type{caseTypes.length !== 1 ? 's' : ''}
                </p>
              </div>

              {/* Target Recipient Dropdown */}
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 px-1">Target Recipient</label>
                <select
                  value={formData.beneficiary_id}
                  onChange={e => setFormData({ ...formData, beneficiary_id: e.target.value })}
                  className="w-full px-5 py-4 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-indigo-500 font-bold text-slate-700"
                  required
                >
                  {beneficiaries.map(b => (
                    <option key={b.id} value={b.id}>
                      {b.full_name} {b.relationship !== 'Self' ? `(${b.relationship})` : ''}
                    </option>
                  ))}
                </select>
                <p className="text-[10px] text-slate-400 mt-2 px-1 italic">
                  Note: Beneficiaries must be pre-registered in your profile.
                </p>
              </div>

              {/* Amount */}
              <div className="pt-4 border-t border-slate-50">
                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 px-1">Policy Amount (KSh)</label>
                <div className="relative">
                  <input
                    type="text"
                    readOnly
                    value={formData.target_amount ? Number(formData.target_amount).toLocaleString() : '---'}
                    className="w-full px-5 py-5 bg-slate-900 border-none rounded-2xl font-mono font-black text-2xl text-emerald-400 cursor-not-allowed"
                  />
                  {fetchingAmount && (
                    <div className="absolute right-5 top-1/2 -translate-y-1/2 flex items-center gap-2 bg-slate-800 px-3 py-1 rounded-lg">
                      <Loader2 className="animate-spin text-indigo-400" size={16} />
                      <span className="text-[10px] font-bold text-white uppercase tracking-tighter">Calculating...</span>
                    </div>
                  )}
                </div>
                <div className="mt-3 flex gap-2 p-3 bg-slate-50 rounded-xl border border-slate-100">
                  <Info size={14} className="text-slate-400 shrink-0 mt-0.5" />
                  <p className="text-[10px] font-medium text-slate-500 leading-normal">
                    The amount is strictly governed by the organization's welfare charter and cannot be modified.
                  </p>
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* Right Section: Attachments & Action (2/5 width) */}
        <div className="lg:col-span-2 space-y-6">
          <section className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-xl shadow-slate-200/50 flex flex-col h-full">
            <h3 className="text-sm font-black uppercase tracking-widest text-indigo-600 mb-6 flex items-center gap-2">
              <Upload size={16} /> Verification
            </h3>

            <div className="flex-1 space-y-6">
              <div className="relative group border-2 border-dashed border-slate-200 hover:border-indigo-400 hover:bg-indigo-50/30 rounded-[2rem] p-8 transition-all text-center">
                <input
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  className="absolute inset-0 opacity-0 cursor-pointer z-10"
                />
                <div className="space-y-3 pointer-events-none">
                  <div className="h-12 w-12 bg-white rounded-2xl shadow-sm border border-slate-100 flex items-center justify-center mx-auto text-indigo-600 group-hover:scale-110 transition-transform">
                    <Upload size={20} />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-slate-700 uppercase tracking-wider">Upload Evidence</p>
                    <p className="text-[10px] text-slate-400 mt-1">Medical reports, certificates, or IDs</p>
                  </div>
                </div>
              </div>

              {/* File List */}
              <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1">
                {files.length === 0 && (
                  <div className="py-8 text-center border border-slate-50 rounded-2xl bg-slate-50/50 border-dashed">
                    <p className="text-[10px] font-bold text-slate-300 uppercase tracking-widest">No documents attached</p>
                  </div>
                )}
                {files.map((file, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-white p-3 rounded-xl border border-slate-100 shadow-sm animate-in zoom-in-95 transition-all hover:border-indigo-200">
                    <div className="flex items-center gap-3">
                      <CheckCircle2 size={14} className="text-emerald-500" />
                      <div className="flex flex-col">
                        <span className="text-[10px] font-black text-slate-700 truncate max-w-[120px] uppercase tracking-tighter">
                          {file.name}
                        </span>
                        <span className="text-[9px] text-slate-400">{(file.size / 1024).toFixed(0)} KB</span>
                      </div>
                    </div>
                    <button 
                      type="button" 
                      onClick={() => removeFile(idx)} 
                      className="p-1.5 hover:bg-rose-50 text-slate-300 hover:text-rose-500 rounded-lg transition-colors"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || !formData.type}
              className="mt-8 w-full bg-indigo-600 text-white py-5 rounded-[2rem] font-black text-xs uppercase tracking-[0.2em] shadow-xl shadow-indigo-100 hover:bg-indigo-700 transition-all disabled:opacity-30 disabled:grayscale active:scale-[0.98] flex items-center justify-center gap-3"
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin" size={18} />
                  <span>Submitting Claim...</span>
                </>
              ) : (
                <>
                  <span>File Application</span>
                  <CheckCircle2 size={16} />
                </>
              )}
            </button>
          </section>
        </div>
      </form>

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
};

export default CreateCase;